import React, { useState, useEffect } from 'react';
import { Typography, Tabs, Card, Alert, Table } from 'antd';
import { LockOutlined, RightOutlined, BellOutlined, SyncOutlined, MenuOutlined } from '@ant-design/icons';
import { API_BASE_URL, EXTERNAL_LINKS } from '../constants';
import { ApiExample } from './CodeExamples';

const { Title, Paragraph, Text, Link } = Typography;
const { TabPane } = Tabs;

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const yOffset = -80; // Account for fixed header
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

const TopNav = () => (
  <div className="top-nav">
    <div className="nav-links">
      <Link href="#">API Reference</Link>
      <Link href="#">Docs</Link>
      <Link href="mailto:support@getzorba.com">Support</Link>
      <Link href="https://buy.getzorba.com" target="_blank" className="sign-in">Sign in →</Link>
    </div>
  </div>
);

const SidebarCategory = ({ title, items, isExpanded, onToggle, onItemClick }) => (
  <div className="sidebar-category">
    <div className={`category-header ${isExpanded ? 'expanded' : ''}`} onClick={onToggle}>
      <RightOutlined className="chevron-icon" />
      {title}
    </div>
    {isExpanded && (
      <div className="category-items">
        {items.map((item, index) => (
          <Link 
            key={index} 
            className="nav-item sub-item"
            onClick={() => onItemClick(`${title.toLowerCase()}-${item.toLowerCase().replace(/\s+/g, '-')}`)}
          >
            {item}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const Sidebar = ({ activeItem, setActiveItem, expandedCategories, toggleCategory, handleItemClick }) => {
  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="logo-container">
          <img 
            src="https://res.cloudinary.com/dyqquolic/image/upload/v1675256922/zorba-logo-black_xwagzk.svg" 
            alt="Zorba" 
          />
          <span className="api-text">API</span>
        </div>
      </div>
      <nav className="nav-section">
        <Link 
          className={`nav-item direct-link ${activeItem === 'introduction' ? 'active' : ''}`}
          onClick={() => handleItemClick('introduction')}
        >
          Introduction
        </Link>
        <Link 
          className={`nav-item direct-link ${activeItem === 'authentication' ? 'active' : ''}`}
          onClick={() => handleItemClick('authentication')}
        >
          Authentication
        </Link>

        <SidebarCategory
          title="Deals"
          items={['List all Deals']}
          isExpanded={expandedCategories.deals}
          onToggle={() => toggleCategory('deals')}
          onItemClick={handleItemClick}
        />
        <SidebarCategory
          title="Webhooks"
          items={['Create a Webhook']}
          isExpanded={expandedCategories.webhooks}
          onToggle={() => toggleCategory('webhooks')}
          onItemClick={handleItemClick}
        />
      </nav>
    </div>
  );
};

const CodeBlock = ({ language, code }) => (
  <Card className="code-block" bordered={false}>
    <pre>
      <code className={`language-${language}`}>{code}</code>
    </pre>
  </Card>
);

const ApiDocs = () => {
  const [activeItem, setActiveItem] = useState('introduction');
  const [expandedCategories, setExpandedCategories] = useState({
    deals: false,
    webhooks: false
  });
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleItemClick = (sectionId) => {
    setActiveItem(sectionId);
    scrollToSection(sectionId);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector('.sidebar');
      const menuToggle = document.querySelector('.menu-toggle');
      
      if (isMobileMenuVisible && 
          sidebar && 
          !sidebar.contains(event.target) && 
          menuToggle && 
          !menuToggle.contains(event.target)) {
        setIsMobileMenuVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuVisible]);

  // Close mobile menu when a link is clicked
  const handleMobileLinkClick = (sectionId) => {
    setIsMobileMenuVisible(false);
    scrollToSection(sectionId);
  };

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = '';
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 100;
        if (window.scrollY >= sectionTop) {
          currentSection = section.id;
        }
      });

      if (currentSection) {
        document.querySelectorAll('.nav-item').forEach(item => {
          item.classList.remove('active');
          if (item.getAttribute('data-section') === currentSection) {
            item.classList.add('active');
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const dealListParams = {
    limit: {
      type: 'integer',
      description: 'Number of deals to return per page (Max: 100)',
      example: '10',
      required: true
    },
    page: {
      type: 'integer',
      description: 'Number of the page',
      example: '0',
      required: true
    }
  };

  const dealListResponse = {
    metadata: {
      total: 128,
      limit: 50,
      page: 1
    },
    deals: [
      {
        address: {
          googlePlaceId: "ChIJ-UjtEtmx2YgRFyZqWj2ug5Q",
          formattedAddress: "13180 NW 11th Ave, North Miami, FL 33168",
          location: [25.8949494, -80.217426],
          state: "Florida",
          stateCode: "FL",
          streetNumber: "13180",
          route: "NW 11th Ave",
          locality: "North Miami",
          county: "Miami-Dade",
          zipcode: "33168",
          kind: "house"
        },
        createdAt: "2025-06-05T13:15:18.466Z",
        _id: "6841986642c8b80008d1e518",
        askingPrice: 349900,
        bedroom: 4,
        bathroom: 1,
        yearBuilt: 1953,
        squareFootage: 1190,
        lotSize: 6500,
        propertyType: "single_family_home",
        hasPool: null,
        parkingSpaces: 0,
        parkingTypes: [],
        wastewaterType: "",
        floodZone: "X",
        arv: 397500,
        grossRent: 3096,
        photos: "https://investorlift.com/p/247002"
      }
      // ... Additional deals would be listed here
    ]
  };

  return (
    <>
      <button 
        className="menu-toggle" 
        onClick={() => setIsMobileMenuVisible(!isMobileMenuVisible)}
      >
        <MenuOutlined />
      </button>

      <TopNav />
      <div className="docs-container">
        <div className={`sidebar ${isMobileMenuVisible ? 'mobile-visible' : ''}`}>
          <div className="sidebar-header">
            <div className="logo-container">
              <img 
                src="https://res.cloudinary.com/dyqquolic/image/upload/v1675256922/zorba-logo-black_xwagzk.svg" 
                alt="Zorba" 
              />
              <span className="api-text">API</span>
            </div>
          </div>
          <nav className="nav-section">
            <Link 
              className={`nav-item direct-link ${activeItem === 'introduction' ? 'active' : ''}`}
              onClick={() => handleMobileLinkClick('introduction')}
            >
              Introduction
            </Link>
            <Link 
              className={`nav-item direct-link ${activeItem === 'authentication' ? 'active' : ''}`}
              onClick={() => handleMobileLinkClick('authentication')}
            >
              Authentication
            </Link>

            <SidebarCategory
              title="Deals"
              items={['List all Deals']}
              isExpanded={expandedCategories.deals}
              onToggle={() => toggleCategory('deals')}
              onItemClick={handleMobileLinkClick}
            />
            <SidebarCategory
              title="Webhooks"
              items={['Create a Webhook']}
              isExpanded={expandedCategories.webhooks}
              onToggle={() => toggleCategory('webhooks')}
              onItemClick={handleMobileLinkClick}
            />
          </nav>
        </div>
        <div className="main-content">
          <section id="introduction">
            <Title level={1}>API Reference</Title>
            
            <Paragraph>
              The Zorba Buyers Marketplace API is organized around <Link href={EXTERNAL_LINKS.REST_WIKI} target="_blank">REST</Link>. 
              Our API has predictable resource-oriented URLs, accepts <Link href={EXTERNAL_LINKS.FORM_ENCODED} target="_blank">form-encoded</Link> request 
              bodies, returns <Link href={EXTERNAL_LINKS.JSON_SPEC} target="_blank">JSON-encoded</Link> responses, and uses standard 
              HTTP response codes, <Link href="#authentication">authentication</Link>, and verbs.
            </Paragraph>

            <Title level={2}>Accessing Investment Opportunities</Title>
            <Paragraph>
              Zorba provides buyers with real-time access to investment opportunities through two mechanisms:
            </Paragraph>
            
            <div className="access-methods">
              <Card className="access-method">
                <Title level={3}><BellOutlined /> Webhook-based push</Title>
                <Paragraph>
                  Get notified when new deals matching your buy box appear or when asking prices are reduced.
                  This is the recommended approach for real-time updates.
                </Paragraph>
                <Link href="#webhooks-subscribe-to-webhooks">Learn more about webhooks →</Link>
              </Card>

              <Card className="access-method">
                <Title level={3}><SyncOutlined /> Polling-based pull</Title>
                <Paragraph>
                  Periodically fetch the most recent deals. This approach is simpler to implement but may not
                  provide immediate updates.
                </Paragraph>
                <Link href="#deals-get-deals">Learn more about polling →</Link>
              </Card>
            </div>

            <div className="base-url">
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Base URL</Text>
              <div className="url">{API_BASE_URL}</div>
            </div>
          </section>

          <section id="authentication">
            <Title level={2}>Authentication</Title>
            <Paragraph>
              The Zorba API uses API keys to authenticate requests. You can view and manage
              your API keys in the Zorba Dashboard.
            </Paragraph>

            <Paragraph>
              Test mode secret keys have the prefix <Text code>zk_test_</Text> and live mode
              secret keys have the prefix <Text code>zk_live_</Text>. Alternatively, you can
              use restricted API keys for granular permissions.
            </Paragraph>

            <Alert
              message="Security Notice"
              description="Your API keys carry many privileges, so be sure to keep them secure! Do not share your secret API keys in publicly accessible areas such as GitHub, client-side code, and so forth."
              type="warning"
              showIcon
            />

            <Title level={3}>Authentication Example</Title>

            <Tabs defaultActiveKey="curl">
              <TabPane tab="cURL" key="curl">
                <CodeBlock
                  language="bash"
                  code={`curl ${API_BASE_URL}/buyers \\
  -H "x-api-key: Bearer zk_test_your_api_key"`}
                />
              </TabPane>
              <TabPane tab="Node.js" key="nodejs">
                <CodeBlock
                  language="javascript"
                  code={`const zorba = require('zorba-node');
const client = new zorba('zk_test_your_api_key');

// All API requests will include the x-api-key header automatically
const buyers = await client.buyers.list();`}
                />
              </TabPane>
              <TabPane tab="Python" key="python">
                <CodeBlock
                  language="python"
                  code={`import zorba

zorba.api_key = 'zk_test_your_api_key'
# All API requests will include the x-api-key header automatically

buyers = zorba.Buyer.list()`}
                />
              </TabPane>
            </Tabs>

            <Alert
              message="Your API Key"
              description="To test requests using your account, replace the sample API key with your actual API key from the Zorba Dashboard."
              type="info"
              showIcon
            />
          </section>

          <section id="deals">
            <Title level={2}>Deals</Title>
            
            <section id="deals-get-deals">
              <Title level={3}>List all Deals</Title>
              <Paragraph>
                Poll Zorba's API for the latest deals that are currently available for sale matching your buy box.
                Use pagination parameters to control the number of results returned.
              </Paragraph>

              <Title level={4}>HTTP Request</Title>
              <CodeBlock
                language="bash"
                code={`POST ${API_BASE_URL}/deal/list`}
              />

              <div className="parameters-header">PARAMETERS</div>
              <Table
                dataSource={Object.entries(dealListParams).map(([key, value]) => ({
                  key,
                  parameter: key,
                  type: value.type,
                  description: value.description,
                  required: value.required
                }))}
                columns={[
                  {
                    title: 'Parameter',
                    dataIndex: 'parameter',
                    key: 'parameter',
                    render: text => <Text code>{text}</Text>
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type'
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description'
                  },
                  {
                    title: 'Required',
                    dataIndex: 'required',
                    key: 'required',
                    render: required => required ? 'Yes' : 'No'
                  }
                ]}
                pagination={false}
              />

              <Title level={4}>Example Request</Title>
              <ApiExample
                endpoint="/deal/list"
                method="POST"
                params={dealListParams}
                response={dealListResponse}
              />

              <Title level={4}>Returns</Title>
              <Paragraph>
                Returns a paginated list of deals. The response includes:
              </Paragraph>
              <ul>
                <li>
                  <Text code>metadata</Text> - Contains pagination information:
                  <ul>
                    <li><Text code>total</Text> - Total number of deals available</li>
                    <li><Text code>start</Text> - Starting index of the current page</li>
                    <li><Text code>offset</Text> - Number of deals per page</li>
                  </ul>
                </li>
                <li>
                  <Text code>deals</Text> - Array of deal objects, each containing:
                  <ul>
                    <li><Text code>address</Text> - Detailed property address information</li>
                    <li><Text code>askingPrice</Text> - The listed price of the property</li>
                    <li><Text code>arv</Text> - After Repair Value estimate</li>
                    <li><Text code>grossRent</Text> - Estimated monthly rental income</li>
                    <li><Text code>propertyType</Text> - Type of property (e.g., single_family_home)</li>
                    <li><Text code>bedroom</Text> - Number of bedrooms</li>
                    <li><Text code>bathroom</Text> - Number of bathrooms</li>
                    <li><Text code>squareFootage</Text> - Living area in square feet</li>
                    <li><Text code>lotSize</Text> - Total lot size in square feet</li>
                    <li><Text code>yearBuilt</Text> - Year the property was built</li>
                    <li><Text code>photos</Text> - URL to property photos</li>
                    <li><Text code>photos</Text> - Property URL </li>
                    <li><Text code>hoaInfo</Text> - HOA informations</li>
                  </ul>
                </li>
              </ul>
            </section>
          </section>

          <section id="webhooks">
            <Title level={2}>Webhooks</Title>
            
            <section id="webhooks-subscribe-to-webhooks">
              <Title level={3}>Create a Webhook</Title>
              <Paragraph>
                Create a new webhook endpoint to receive real-time notifications when new deals matching your buy box become available or when existing deals have price reductions.
              </Paragraph>

              <Alert
                message="Quick Setup via Dashboard"
                description={
                  <span>
                    You can quickly set up webhooks through the Zorba Dashboard at{' '}
                    <Link href="https://buy.getzorba.com/settings/webhooks" target="_blank">
                      Settings → Webhooks
                    </Link>
                    . This is the recommended method if you don't need programmatic webhook creation.
                  </span>
                }
                type="info"
                showIcon
              />

              <Title level={4}>HTTP Request</Title>
              <CodeBlock
                language="bash"
                code={`POST ${API_BASE_URL}/webhook/create`}
              />

              <div className="parameters-header">PARAMETERS</div>
              <Table
                dataSource={[{
                  key: 'url',
                  parameter: 'url',
                  type: 'string',
                  description: 'The URL that will be called when a matching deal is found',
                  required: true
                }, 
                {
                  key: 'eventType',
                  parameter: 'eventType',
                  type: 'string',
                  description: 'Event type to subscribe to. Options: new_offer, price_drop',
                  required: true
                }, 
              ]
              }
                columns={[
                  {
                    title: 'Parameter',
                    dataIndex: 'parameter',
                    key: 'parameter',
                    render: text => <Text code>{text}</Text>
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type'
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description'
                  },
                  {
                    title: 'Required',
                    dataIndex: 'required',
                    key: 'required',
                    render: required => required ? 'Yes' : 'No'
                  }
                ]}
                pagination={false}
              />

              <Title level={4}>Example Request</Title>
              <ApiExample
                endpoint="/webhook/create"
                method="POST"
                params={{
                  url: {
                    type: 'string',
                    description: 'Webhook URL',
                    example: 'https://api.your-company.com/zorba-webhook',
                    required: true
                  }
                }}
                response={{
                  id: "wh_1234567890",
                  url: "https://api.your-company.com/zorba-webhook",
                  status: "active",
                  created: "2024-03-20T15:30:00Z"
                }}
              />

              <Title level={4}>Returns</Title>
              <Paragraph>
                Returns the created webhook object. The response includes:
              </Paragraph>
              <ul>
                <li><Text code>id</Text> - Unique identifier for the webhook</li>
                <li><Text code>url</Text> - The URL that will be called</li>
                <li><Text code>status</Text> - Current status of the webhook (active or inactive)</li>
                <li><Text code>created</Text> - Timestamp when the webhook was created</li>
              </ul>

              <Alert
                message="Security Notice"
                description="Make sure your webhook endpoint is properly secured and can handle the expected request volume. We recommend using HTTPS for your webhook URL."
                type="warning"
                showIcon
              />
            </section>
          </section>
        </div>
      </div>
    </>
  );
};

export default ApiDocs; 